import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/Common/button';

interface ErrorStateProps {
  error: string;
  onRetry?: () => void;
  title?: string;
}

export const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry, title }) => {
  const { t } = useTranslation();
  return (
    <div className="p-4 rounded-lg border bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
        <div className="flex-1 space-y-2">
          {title && <div className="font-semibold text-red-800 dark:text-red-200">{title}</div>}
          <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
          {onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
              <RefreshCw className="h-3 w-3 mr-1" />
              {t('global.retry')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

interface InlineErrorProps {
  error: string;
  onRetry?: () => void;
}

export const InlineError: React.FC<InlineErrorProps> = ({ error, onRetry }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center p-8">
      <div className="text-center space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
        <div className="space-y-2">
          <h3 className="font-semibold text-lg">{t('global.somethingWentWrong')}</h3>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
        {onRetry && (
          <Button onClick={onRetry} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('global.retry')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ErrorState;
